import React, {Component} from 'react';
import PropTypes from 'prop-types';
import {i18n, formatDate} from '../../Utility/Format';
import {getCcyPairsFromParameters} from '../../Utility/CurrencyPairHelper';
import Button from '../Controls/Button';
import Icon from '../Helper/Icon';
import Checkbox from '../Controls/Checkbox';
import Switch from '../Controls/Switch';
import Calendar, {DatePicker} from '../Widgets/Calendar';
import CurrencyPairOptions from './CurrencyPairOptions';
import {xor, includes} from 'lodash';
import moment from 'moment';

const ALL_TENORS = [
    "ON", "TN", "SN",
    "1W", "2W", "1M",
    "2M", "3M", "4M",
    "5M", "6M", "9M",
    "12M", "18M", "24M",
    "36M", "48M", "60M"
];

export default class ForwardOptions extends Component {

    static propTypes = {
        currencies: PropTypes.array,
        options: PropTypes.object.isRequired,
        locale: PropTypes.string.isRequired,

        onCcyPairsChange: PropTypes.func.isRequired,
        onTenorsChange: PropTypes.func.isRequired,
        onBrokenDateAdd: PropTypes.func.isRequired,
        onBrokenDateRemove: PropTypes.func.isRequired,
        onSwapPointsToggle: PropTypes.func.isRequired,
        onMenuToggle: PropTypes.func.isRequired,
        onReset: PropTypes.func.isRequired,
    };

    constructor() {
        super();
        this.state = {
            calendarVisible: false
        };
    }

  componentDidMount() { 
  this.observer = new MutationObserver(() => {
    this.removeAriaRoles();
    this.fixRcCalendarRoles();
    this.fixInvalidTabIndex();
    this.fixMissingRoles();
    this.fixCalendarAria();
});
 
  this.observer.observe(document.body, {
    childList: true,
    subtree: true
  });
}
 
componentWillUnmount() {
  if (this.observer) {
    this.observer.disconnect();
  }
}

fixCalendarAria = () => {
  document.querySelectorAll('.rc-calendar-date').forEach(el => {
    // ❌ Remove invalid aria-selected
    el.removeAttribute('aria-selected');
 
    // ✅ Add correct accessible label instead
    const dateText = el.innerText;
    if (dateText) {
      el.setAttribute('aria-label', `Select date ${dateText}`);
    }
  });
};

fixInvalidTabIndex = () => {
  // ❌ Remove tabindex from main calendar container
  document.querySelectorAll('.rc-calendar-calendar').forEach(el => {
    el.removeAttribute('tabindex');
  });
 
  // ❌ Remove from table wrapper if present
  document.querySelectorAll('.rc-calendar-table').forEach(el => {
    el.removeAttribute('tabindex');
  });
 
  // ❌ Remove from any non-clickable wrapper divs
  document.querySelectorAll('div[tabindex="0"]').forEach(el => {
    // Keep only actual date cells
    if (!el.classList.contains('rc-calendar-date')) {
      el.removeAttribute('tabindex');
    }
  });
};
 
removeAriaRoles = () => {
  document.querySelectorAll(`
    [role="grid"],
    [role="row"],
    [role="gridcell"],
    [role="table"],
    [role="columnheader"],
    [role="rowgroup"]
  `).forEach(el => {
    el.removeAttribute('role');
  });
};

fixMissingRoles = () => {
  document.querySelectorAll('[tabindex="0"]').forEach(el => {
    const tag = el.tagName.toLowerCase();
 
    // Skip native elements (already accessible)
    if (tag === 'button') return;
 
    // If it's <a> without href → needs role
    if (tag === 'a' && !el.getAttribute('href')) {
      el.setAttribute('role', 'button');
      return;
    }
 
    // If it's calendar date
    if (el.classList.contains('rc-calendar-date')) {
      el.setAttribute('role', 'button');
      return;
    }
 
    // If it's navigation or selectors
    if (
      el.className.includes('rc-calendar-prev') ||
      el.className.includes('rc-calendar-next') ||
      el.className.includes('rc-calendar-month') ||
      el.className.includes('rc-calendar-year')
    ) {
      el.setAttribute('role', 'button');
    }
  });
};

fixRcCalendarRoles = () => {
  // ❌ Remove wrong role from main container
  document.querySelectorAll('.rc-calendar-calendar').forEach(el => {
    el.removeAttribute('role');
  });
 
  // ❌ Also remove from table wrapper if exists
  document.querySelectorAll('.rc-calendar-table').forEach(el => {
    el.removeAttribute('role');
  });
 
  // ✅ Add role ONLY to actual date cells
  document.querySelectorAll('.rc-calendar-date').forEach(el => {
    el.setAttribute('role', 'button');
  });
 
  // ✅ Fix navigation buttons (already <a>, just ensure role)
  document.querySelectorAll(
    '.rc-calendar-prev-month-btn, .rc-calendar-next-month-btn, .rc-calendar-prev-year-btn, .rc-calendar-next-year-btn'
  ).forEach(el => {
    el.setAttribute('role', 'button');
  });
};

    render() {
        const toggleCalendar = () => {
            this.setState({calendarVisible: !this.state.calendarVisible});
        };

        const addCurrencyPairOption = () => {
            const newCcyPair = this.props.options.ccyPairs[0] || 'EUR_USD';
            const changedCcyPairs = this.props.options.ccyPairs.concat([newCcyPair]);
            this.props.onCcyPairsChange(changedCcyPairs);
        };

        const changeCurrencyPairOption = (index, status, newValue) => {
            const ccyPair = this.props.options.ccyPairs[index];
            const changedCcyPairs = this.props.options.ccyPairs.slice();

            if(status === 'base') {
                changedCcyPairs[index] = newValue.isoString + ccyPair.substr(3);
            } else {
                changedCcyPairs[index] = ccyPair.substr(0,4) +  newValue.isoString;
            }

            this.props.onCcyPairsChange(changedCcyPairs);
        };

        const removeCurrencyPairOption = (index) => {
            const changedCcyPairs = this.props.options.ccyPairs.slice();
            changedCcyPairs.splice(index, 1);
            this.props.onCcyPairsChange(changedCcyPairs);
        };

        const toggleTenor = (tenor) => {
            const changedTenors = xor(this.props.options.tenors, [tenor]);
            this.props.onTenorsChange(changedTenors);
        };

        const addBrokenDate = (date) => {
            const dateString = date.format('YYYY-MM-DD');
            toggleCalendar();
            this.props.onBrokenDateAdd(dateString);
        };

        const removeBrokenDate = (date) => {
            this.props.onBrokenDateRemove(date);
        };

        const toggleSwapPoints = () => {
            this.props.onSwapPointsToggle(!this.props.options.swapPoints);
        };

        const toggleMenu = (e) => {
            this.props.onMenuToggle(e, this.props.options);
        };

        const resetOptions = () => {
            this.props.onReset();
        };

         return (
            <div>
                <section>
                    <p>{i18n('dbm.forward.options.chooseCurrencyPairs')}</p>
                    <CurrencyPairOptions
                        currencyPairs={getCcyPairsFromParameters(this.props.options.ccyPairs, this.props.currencies)}
                        currencies={this.props.currencies}
                        onChangeCurrencyPair={changeCurrencyPairOption}
                        onRemoveCurrencyPair={removeCurrencyPairOption}
                        showRemoveButton={true}
                        locale={this.props.locale}/>
                    <Button onClick={addCurrencyPairOption}>
                        <Icon name="plus" isCollapsed={true}/> {i18n('dbm.forward.options.addNewCurrencyPairButton')}
                    </Button>
                </section>

                <section>
                    <p>{i18n('dbm.forward.options.choosePeriod')}</p>
                        {
                            ALL_TENORS.map((tenor, index) => {
                                return (
                                    <Checkbox key={'period-' + index}
                                        label={tenor}
                                        isSelected={includes(this.props.options.tenors, tenor)}
                                        onClick={() => {toggleTenor(tenor)}}
                                    />
                                )
                            })
                        }
                    <div className="u-clearer"></div>
                </section>
                
                <section>
                    <p>{i18n('dbm.forward.options.brokenDate')}</p>
                    <div className="settlement-options">
                        <DatePicker value={moment()} onClick={toggleCalendar} locale={this.props.locale}/>
                        <div>
                            { this.state.calendarVisible ?
                                <Calendar
                                    fullBorders={true}
                                    showToday={false}
                                    showDateInput={false}
                                    locale={this.props.locale}
                                    showHighlights={false}
                                    onSelect={addBrokenDate}
                                    disabledDate={(d) => {return d < moment()}}
                                /> : null
                            }
                        </div>
                    </div>
                    <div>
                        {
                            this.props.options.brokenDates.map((date) => {
                                return (
                                    <div key={date} className="brokenDate">
                                        {formatDate(date, this.props.locale)}
                                        <Button className="brokenDate__remove-button" aria-label="Remove button broken date" isSquare={true}  onClick={() => {
                                            removeBrokenDate(date)
                                        }}>
                                            <Icon name="close" isCollapsed={true} isSmall={true}/>
                                        </Button>
                                    </div>
                                )
                            })
                        }
                    </div>
                </section>

                <section>
                    <p>{i18n('dbm.forward.options.appearanceAskBid')}</p>
                    <div>
                        <Switch
                            onChange={toggleSwapPoints}
                            toggleOn={!this.props.options.swapPoints}
                            leftLabel={i18n('dbm.forward.options.appearanceSwapPoints')}
                            rightLabel={i18n('dbm.forward.options.appearanceFixings')}
                        />
                    </div>
                </section>
                <div className="drawerPanel__panel__content--footer">
                    <Button
                        onClick={(e) => toggleMenu(e)}>
                        <Icon name="check-lg" isCollapsed={true}/> {i18n('dbm.forward.options.saveOptionsButton')}
                    </Button>
                    <Button
                        className="button button--borderless"
                        onClick={resetOptions}>
                        <Icon name="reset" isCollapsed={true}/> {i18n('dbm.forward.options.resetOptionsButton')}
                    </Button>
                </div>
            </div>
        );
    }
};